import { PersistenceLevel } from "../../config/PersistenceLevels";
import BaseEntity from "../entity/BaseEntity";
import Entity from "../entity/Entity";
import { stepToward } from "../util/MathUtil";

export interface SlowMoEffectEntity extends Entity {
  getDesiredSlowMo(): number;
}

/**
 * Manages slow motion effects in the game by finding all SlowMoEffect entities
 * and calculating the minimum desired slow-mo value, then smoothly ramping to it.
 *
 * This singleton entity automatically queries for entities tagged "slowMoEffect"
 * each tick and updates the game's slow-mo value accordingly.
 */
export class SlowMoManager extends BaseEntity implements Entity {
  id = "slowMoManager";
  persistenceLevel = PersistenceLevel.Permanent;

  private targetValue: number = 1.0;
  private currentValue: number = 1.0;
  private _rampSpeed: number = 8.0;

  /**
   * Creates a new SlowMoManager.
   * @param rampSpeed - How quickly to ramp between slow-mo values (units per second)
   */
  constructor(rampSpeed: number = 8.0) {
    super();
    this._rampSpeed = rampSpeed;
  }

  /**
   * Called each tick to update the slow-mo value based on active effects.
   * Finds all SlowMoEffect entities and smoothly ramps to the minimum value.
   */
  onTick(dt: number) {
    if (!this.game) return;

    const effectEntities = this.game.entities.getTagged(
      "slowMoEffect",
    ) as SlowMoEffectEntity[];
    this.targetValue = this.calculateMinimumValue(effectEntities);

    if (this.currentValue !== this.targetValue) {
      this.currentValue = stepToward(
        this.currentValue,
        this.targetValue,
        this._rampSpeed * dt,
      );
      this.game.slowMo = this.currentValue;
    }
  }

  /**
   * Calculates the minimum slow-mo value from all active effects.
   * Always includes 1.0 (normal speed) in the calculation.
   */
  private calculateMinimumValue(effectEntities: SlowMoEffectEntity[]): number {
    return Math.min(
      1.0,
      ...effectEntities.map((effect) => effect.getDesiredSlowMo()),
    );
  }
}
